import React, { useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import { toast } from "sonner";
import { BellRing, X } from "lucide-react";
import { API } from "@/config";
import { imgUrl } from "@/config";

const iso = (d) => (d ? format(d, "yyyy-MM-dd") : "");

export const StockRequestModal = ({ product, range, onClose }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", start_date: iso(range?.from), end_date: iso(range?.to) });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.start_date || !form.end_date) {
      toast.error("Name, email and dates are required.");
      return;
    }
    if (form.end_date < form.start_date) { toast.error("Return date must be after delivery date."); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/stock-requests`, { product_id: product.id, ...form });
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't send your request — please try again.");
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full bg-black/30 border border-[#FFD166]/25 text-[#F6F1E4] px-3 py-2 rounded-lg text-sm focus:outline-none focus:border-[#FFD166]";

  return (
    <div className="fixed inset-0 z-[110] bg-[#040A0F]/85 backdrop-blur-md flex items-center justify-center p-4" onClick={onClose} data-testid="stock-request-modal">
      <div className="glass w-full max-w-md p-6 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-3 right-3 text-[#B9C8D5] hover:text-[#FFD166] p-2" aria-label="Close" data-testid="stock-request-close">
          <X className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-3 pr-8">
          <img src={imgUrl(product.image)} alt={product.name} className="w-14 h-14 rounded-lg object-contain bg-black/30 p-1" />
          <div>
            <p className="eyebrow text-[#F4A93A]">Sold out for your dates</p>
            <h3 className="font-serif text-xl text-[#F6F1E4] leading-tight">{product.name}</h3>
          </div>
        </div>

        {done ? (
          <div className="mt-6 text-center py-6" data-testid="stock-request-success">
            <BellRing className="w-8 h-8 mx-auto text-[#FFD166] mb-3" />
            <h4 className="font-serif text-2xl text-[#F6F1E4]">You&rsquo;re on the list ✨</h4>
            <p className="text-sm text-[#B9C8D5] mt-2">
              We&rsquo;ll email <span className="text-[#F6F1E4]">{form.email}</span> the moment a {product.name} frees up for {form.start_date} → {form.end_date}.
            </p>
            <button type="button" onClick={onClose} className="btn-primary mt-5 !py-2 !px-5 text-sm" data-testid="stock-request-done">Keep browsing</button>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-5 space-y-3">
            <p className="text-xs text-[#B9C8D5]">Leave your details and we&rsquo;ll automatically email you if one becomes available for your dates.</p>
            <input className={inputCls} placeholder="Your name" value={form.name} onChange={set("name")} required data-testid="stock-request-name" />
            <input className={inputCls} type="email" placeholder="Email" value={form.email} onChange={set("email")} required data-testid="stock-request-email" />
            <input className={inputCls} type="tel" placeholder="Phone (optional)" value={form.phone} onChange={set("phone")} data-testid="stock-request-phone" />
            <div className="grid grid-cols-2 gap-3">
              <label className="text-[11px] text-[#B9C8D5]">Delivery
                <input className={`${inputCls} mt-1`} type="date" value={form.start_date} onChange={set("start_date")} required data-testid="stock-request-start" />
              </label>
              <label className="text-[11px] text-[#B9C8D5]">Return
                <input className={`${inputCls} mt-1`} type="date" value={form.end_date} onChange={set("end_date")} required data-testid="stock-request-end" />
              </label>
            </div>
            <button type="submit" disabled={busy} className="btn-primary w-full !py-2.5 text-sm disabled:opacity-60" data-testid="stock-request-submit">
              {busy ? "Sending…" : "Notify me when it's back"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};
